import React, { FC, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import {
  AppLanguage,
  defaultLanguage,
  languages,
  languageStorageKey,
} from 'core/intl/languages';
import { QueryKey } from 'api/queryKey';
import { UsersAPI } from 'api/UsersAPI';
import { useQuery } from 'react-query';
import { extractSettingsLanguage, getLanguageCode } from 'core/intl/intlUtils';
import { useSelector } from 'react-redux';
import {
  isAuthenticatedSelector,
  needMfaVerificationSelector,
} from 'state/auth/auth.selectors';

interface AppIntlContextProps {
  handleLocalLanguageChange?: (value: string) => void;
  currentLanguage: AppLanguage;
}

export const AppIntlContext = React.createContext<AppIntlContextProps>({
  currentLanguage: defaultLanguage,
});

export const AppIntlProvider: FC = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<AppLanguage>(
    defaultLanguage
  );
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const needMfaVerification = useSelector(needMfaVerificationSelector);

  const { data } = useQuery(QueryKey.UserSettings, UsersAPI.getUserSettings, {
    enabled: !needMfaVerification && isAuthenticated,
  });

  const setCurrentLanguageByCode = (code: string) => {
    const lang = languages.find((l) => l.code === code);

    if (lang) {
      setCurrentLanguage(lang);
    }
  };

  useEffect(() => {
    if (data) {
      const settingsLang = extractSettingsLanguage(data);

      settingsLang && setCurrentLanguageByCode(settingsLang);
    } else {
      const browserLang = getLanguageCode(navigator.language);
      const localStorageLang = localStorage.getItem(languageStorageKey);

      localStorageLang
        ? setCurrentLanguageByCode(localStorageLang)
        : browserLang && setCurrentLanguageByCode(browserLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleLocalLanguageChange = (value: string) => {
    setCurrentLanguageByCode(value);
    localStorage.setItem(languageStorageKey, value);
  };

  return (
    <AppIntlContext.Provider
      value={{ handleLocalLanguageChange, currentLanguage }}
    >
      <IntlProvider
        defaultLocale={defaultLanguage.name}
        locale={currentLanguage.name}
        messages={currentLanguage.translations}
      >
        {children}
      </IntlProvider>
    </AppIntlContext.Provider>
  );
};
